import React, { useState } from "react";

import { BigNumber } from "bignumber.js";

import { Button, Card, Icon, Modal, Notification } from "@stellar/design-system";

import { NotificationWithButtons } from "@/components/NotificationWithButtons";

import { formatUploadedFileDisplayName } from "@/helpers/formatUploadedFileDisplayName";

import { useRedux } from "@/hooks/useRedux";

import { ReceiversAside, formatDisbursementAmount } from "./ReceiversAside";
import { SummaryRow } from "./SummaryRow";

import { Disbursement, hasWallet, VerificationFieldMap } from "@/types";

interface StepConfirmationProps {
  draftDetails?: Disbursement;
  csvFile?: File;
  futureBalance: number;
  availableBalance?: string;
  language?: string;
  isResponseSuccess: boolean;
  isDraftEnabled: boolean;
  isSavePending: boolean;
  isApprovalRequired: boolean;
  disbursementDraftsStatus: string;
  notificationRef: React.RefObject<HTMLDivElement | null>;
  onConfirmAndSend: () => void;
  onViewDetails: () => void;
  onBack: () => void;
  onSaveDraft: () => void;
  onStartNew: () => void;
  onGoToDisbursements: () => void;
  onGoToDrafts: () => void;
}

export const StepConfirmation = ({
  draftDetails,
  csvFile,
  futureBalance,
  availableBalance,
  language,
  isResponseSuccess,
  isDraftEnabled,
  isSavePending,
  isApprovalRequired,
  disbursementDraftsStatus,
  notificationRef,
  onConfirmAndSend,
  onViewDetails,
  onBack,
  onSaveDraft,
  onStartNew,
  onGoToDisbursements,
  onGoToDrafts,
}: StepConfirmationProps) => {
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const { organization } = useRedux("organization");
  const languageLabels: Record<string, string> = Object.fromEntries(
    organization.data.supportedLanguages.map(({ code, label }) => [code, label]),
  );

  const missingFields: string[] = [];

  if (!draftDetails?.name?.trim()) missingFields.push("Disbursement name");
  if (!draftDetails?.asset?.id) missingFields.push("Asset");
  if (!draftDetails?.registrationContactType) missingFields.push("Registration contact type");

  const isWalletAddressType = hasWallet(draftDetails?.registrationContactType);
  if (!isWalletAddressType && !draftDetails?.wallet?.id) missingFields.push("Wallet provider");
  if (
    !isWalletAddressType &&
    !draftDetails?.verificationField
  ) {
    missingFields.push("Verification type");
  }

  if (!csvFile) missingFields.push("CSV file");

  const isInsufficientBalance = BigNumber(futureBalance).lt(0);
  const isSubmitDisabled = isApprovalRequired || missingFields.length > 0 || isInsufficientBalance;

  const verificationLabel = draftDetails?.verificationField
    ? VerificationFieldMap[draftDetails.verificationField] || draftDetails.verificationField
    : "-";

  const languageLabel = language ? languageLabels[language] ?? language : "-";

  const modalAmountDisplay = formatDisbursementAmount(
    draftDetails?.stats?.totalAmount ?? "0",
    draftDetails?.asset?.code,
  );

  const renderButtons = () => {
    if (isResponseSuccess) {
      return (
        <div className="DisbursementButtons">
          <Button
            variant="tertiary"
            size="md"
            icon={<Icon.ArrowLeft />}
            iconPosition="left"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault();
              onGoToDisbursements();
            }}
          >
            Back to disbursements
          </Button>
          <div className="DisbursementButtons--continue">
            <Button
              variant="tertiary"
              size="md"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault();
                onGoToDrafts();
              }}
            >
              Go to drafts
            </Button>
            <Button
              variant="primary"
              size="md"
              icon={<Icon.Plus />}
              iconPosition="right"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault();
                onStartNew();
              }}
            >
              Create a new disbursement
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="DisbursementButtons">
        <Button
          variant="tertiary"
          size="md"
          icon={<Icon.ArrowLeft />}
          iconPosition="left"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            onBack();
          }}
          disabled={isSavePending}
        >
          Back
        </Button>
        <div className="DisbursementButtons--continue">
          <Button
            variant="tertiary"
            size="md"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault();
              onSaveDraft();
            }}
            disabled={!isDraftEnabled || isSavePending}
            isLoading={isSavePending}
          >
            Save draft
          </Button>
          <Button
            variant="primary"
            size="md"
            icon={<Icon.CheckCircle />}
            iconPosition="right"
            disabled={isSubmitDisabled || isSavePending}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault();
              setIsConfirmModalOpen(true);
            }}
            {...(isApprovalRequired
              ? {
                  title:
                    "Your organization requires disbursements to be approved by another user. Save as a draft and make sure another user reviews and submits.",
                }
              : {})}
          >
            Confirm disbursement
          </Button>
        </div>
      </div>
    );
  };

  return (
    <>
      {isResponseSuccess ? (
        <NotificationWithButtons
          ref={notificationRef}
          variant="success"
          title="Your disbursement is live"
          buttons={[{ label: "View Disbursement", onClick: onViewDetails }]}
        >
          Stellar Disbursement Platform is sending invitations to unregistered receivers. Payments
          to registered accounts will begin automatically.
        </NotificationWithButtons>
      ) : null}

      <form
        onSubmit={(e: React.FormEvent<HTMLFormElement>) => e.preventDefault()}
        className="DisbursementForm"
      >
        {missingFields.length > 0 ? (
          <Notification variant="warning" title="Missing required information" isFilled>
            The following fields are required before submitting: {missingFields.join(", ")}. Please
            go back and fill them in.
          </Notification>
        ) : null}

        {isInsufficientBalance && missingFields.length === 0 ? (
          <Notification variant="error" title="Insufficient balance" isFilled>
            Your available balance is less than the disbursement amount. Please fund your account
            before submitting.
          </Notification>
        ) : null}

        <div className="DisbursementReceiversLayout DisbursementReceiversLayout--with-aside">
          <div className="DisbursementReceiversMain">
            <SummaryRow
              walletName={draftDetails?.wallet?.name}
              assetCode={draftDetails?.asset?.code}
              registrationContactType={draftDetails?.registrationContactType}
            />

            <Card>
              <div className="DisbursementReviewList">
                <div className="DisbursementReviewList__row">
                  <div className="DisbursementReviewList__label">Disbursement name</div>
                  <div className="DisbursementReviewList__value">{draftDetails?.name ?? "-"}</div>
                </div>
                <div className="DisbursementReviewList__row">
                  <div className="DisbursementReviewList__label">Language</div>
                  <div className="DisbursementReviewList__value">{languageLabel}</div>
                </div>
                <div className="DisbursementReviewList__row">
                  <div className="DisbursementReviewList__label">Verification type</div>
                  <div className="DisbursementReviewList__value">{verificationLabel}</div>
                </div>
                {csvFile ? (
                  <div className="DisbursementReviewList__row">
                    <div className="DisbursementReviewList__label">Receiver CSV file</div>
                    <div className="DisbursementReviewList__value DisbursementReviewList__value--file">
                      <Icon.File02 />
                      <span>{formatUploadedFileDisplayName(csvFile)}</span>
                    </div>
                  </div>
                ) : null}
              </div>
            </Card>
          </div>

          <ReceiversAside
            numberOfReceivers={draftDetails?.stats?.paymentsTotalCount}
            availableBalance={availableBalance}
            disbursementAmount={draftDetails?.stats?.totalAmount}
            futureBalance={futureBalance}
            assetCode={draftDetails?.asset?.code}
            hasUpload={true}
          />
        </div>

        {renderButtons()}
      </form>

      <Modal visible={isConfirmModalOpen} onClose={() => setIsConfirmModalOpen(false)}>
        <Modal.Heading>Ready to disburse?</Modal.Heading>
        <Modal.Body>
          <p>
            You are about to initiate a disbursement of <strong>{modalAmountDisplay}</strong> to{" "}
            <strong>{draftDetails?.stats?.paymentsTotalCount ?? 0} recipients</strong>.
          </p>
          <p>
            Once recipients register, their funds will be held. You won&apos;t be able to change the
            verification method after invitations are sent.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="tertiary" size="md" onClick={() => setIsConfirmModalOpen(false)}>
            Go back
          </Button>
          <Button
            variant="primary"
            size="md"
            isLoading={disbursementDraftsStatus === "PENDING"}
            onClick={() => {
              setIsConfirmModalOpen(false);
              onConfirmAndSend();
            }}
          >
            Confirm and send
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
